import { FC, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from "@mui/material";
import { Add, Send, Close } from "@mui/icons-material";
import { CommentDto } from "../../../api/Api";
import {
  buildCommentDto,
  getCurrentCommentAuthor,
  renderCommentText
} from "../../../utils/commentUtils";
import { MentionInput } from "../mentionInput";
import dayjs from "dayjs";

// The root comment card for a counterparty or a broker. Both pages used to keep their own
// near-identical copy, which is how brokers ended up rendering raw @[Name](email) markup for a
// while: mentions were added to the counterparty copy only. Keep this shared, and note it is a
// different component from commentsSection (the inline thread inside the lifecycle dialogs).
type Props = {
  comments?: CommentDto[] | null;
  onAddComment?: (comment: CommentDto) => void;
  onDeleteComment?: (timestamp: string) => void;
  readOnly?: boolean;
};

export const CommentsCard: FC<Props> = ({
  comments = [],
  onAddComment,
  onDeleteComment,
  readOnly = false
}) => {
  const currentUser = getCurrentCommentAuthor();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [localComments, setLocalComments] = useState(
    comments?.map((x) => x).reverse() ?? []
  );

  const handleAddComment = () => {
    const trimmedComment = newComment.trim();
    if (trimmedComment) {
      const commentDto = buildCommentDto(trimmedComment);
      if (onAddComment) {
        onAddComment(commentDto);
      }
      setNewComment("");
      setLocalComments([commentDto, ...localComments]);
      setDialogOpen(false);
    }
  };

  const handleCancel = () => {
    setNewComment("");
    setDialogOpen(false);
  };

  const handleDeleteComment = (timestamp: string) => {
    if (onDeleteComment) {
      onDeleteComment(timestamp);
    }
    setLocalComments((prev) => prev.filter((c) => c.timestamp !== timestamp));
  };

  return (
    <Box padding={1}>
      <Card
        elevation={5}
        sx={{
          overflow: "hidden",
          backgroundColor: (theme) => theme.palette.background.paper
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 2
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h5">Comments</Typography>
              <Typography variant="body2" color="text.secondary">
                ({localComments.length})
              </Typography>
            </Box>

            {!readOnly && (
              <Button
                variant="contained"
                size="small"
                endIcon={<Add />}
                onClick={() => setDialogOpen(true)}
                sx={{
                  paddingX: { xs: 1, sm: 2 },
                  paddingY: { xs: 0.25, sm: 0.5 },
                  fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                  minWidth: 0,
                  flexShrink: 0
                }}
              >
                Add Comment
              </Button>
            )}
          </Box>
          <Divider sx={{ marginBottom: 2 }} />

          {localComments.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.25,
                maxHeight: 210,
                overflowY: "auto",
                paddingRight: 1
              }}
            >
              {localComments.map((comment, index) => {
                const formattedTimestamp = dayjs(comment.timestamp).format(
                  "DD/MM/YY HH:mm"
                );
                return (
                  <Box key={`${comment.author || "unknown"}-${index}`}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        paddingTop: 1.5,
                        paddingBottom: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: 1,
                          flexWrap: "wrap"
                        }}
                      >
                        <Typography variant="body2">
                          {comment.authorName || comment.author || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formattedTimestamp}:
                        </Typography>
                        {!readOnly &&
                          onDeleteComment &&
                          comment.author === currentUser &&
                          comment.timestamp && (
                            <Tooltip title="Delete comment">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleDeleteComment(comment.timestamp!)
                                }
                                sx={{ marginLeft: "auto", padding: 0.25 }}
                              >
                                <Close fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                      <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                        {renderCommentText(
                          comment.comment ?? "",
                          (display, _email, key) => (
                            <Box
                              component="span"
                              key={key}
                              sx={{
                                color: "primary.main",
                                backgroundColor: "action.selected",
                                borderRadius: "3px",
                                padding: "0 3px"
                              }}
                            >
                              @{display}
                            </Box>
                          )
                        )}
                      </Typography>
                    </Box>
                    {index < localComments.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                paddingTop: 3,
                paddingBottom: 3
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No comments yet
              </Typography>
            </Box>
          )}
        </CardContent>

        <Dialog
          open={dialogOpen}
          onClose={handleCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle variant="h5">Add New Comment</DialogTitle>
          <DialogContent>
            <Box sx={{ marginTop: 1 }}>
              <MentionInput
                value={newComment}
                onChange={setNewComment}
                placeholder="Enter your comment here..."
                multiline
                rows={4}
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button variant="text" onClick={handleCancel} color="primary">
              <Typography
                variant="button"
                lineHeight={1}
                color="text.secondary"
              >
                Cancel
              </Typography>
            </Button>
            <Button
              variant="contained"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
              endIcon={<Send />}
              sx={{
                display: "flex",
                alignItems: "center"
              }}
            >
              Add comment
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
};
